import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { PiToggleRightThin, PiToggleLeftThin } from 'react-icons/pi';
import { toast } from 'react-toastify';
import { useUserRole } from '../../context/UserContext';
import {
  updatePermissionApi as updatePermission,
  fetchPermissionsApi,
  fetchAllGroupApi as fetchGroup,
} from '../../services/permissionServices';

interface Group {
  id: string;
  name: string;
  permissions: Record<string, boolean>;
}

const PermissionPages = () => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groupList, setGroupList] = useState<Group[]>([]);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const { role } = useUserRole();
  const isSuperAdmin = role === 'Super Admin';

  const modelName = useMemo(
    () => [
      'RCM',
      'Risk Catalog',
      'Risk Residual',
      'Control Catalog',
      'Control Test',
      'KRI Catalog',
      'KRI Heatmap',
      'Incidents Catalog',
      'ActionPlan',
      'Approval Sheet',
      'Settings',
      'User Management',
      'Notifications',
      'Dashboard',
      'Disclosure',
    ],
    []
  );

  useEffect(() => {
    fetchGroupList();
  }, []);

  const fetchGroupList = async () => {
    try {
      const data = await fetchGroup();
      if (Array.isArray(data.data)) {
        setGroupList(data.data);
      } else {
        throw new Error('Invalid groups format');
      }
    } catch (error) {
      console.error('Error fetching group list:', error);
      toast.error('Failed to fetch group list');
    }
  };

  const handleGroupChange = async (groupName: string) => {
    setSelectedGroup(groupName);
    const group = groupList.find((g) => g.name === groupName);
    if (group && group.permissions) {
      setPermissions(group.permissions);
    } else {
      setPermissions({});
    }
  };

  const handleToggle = (modelName: string, type: string) => {
    setPermissions((prev) => ({
      ...prev,
      [`${modelName}_${type}`]: !prev[`${modelName}_${type}`],
    }));
  };

  const submitPermissionGroup = async () => {
    if (!selectedGroup) {
      toast.error('Please select a group');
      return;
    }
    setLoading(true);
    try {
      const group = groupList.find((g) => g.name === selectedGroup);
      if (!group) {
        toast.error('Group not found');
        return;
      }
      const response :any= await updatePermission(group.id, {
        name: selectedGroup,
        permissions,
      });
      if (response?.success) {
        toast.success('Permissions updated successfully');
      } else {
        toast.error('Failed to update permissions');
      }
    } catch (error) {
      toast.error('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem active>{t('permission.Permission')}</BreadcrumbItem>
      </Breadcrumb>

      <div className="rounded p-4 bg-white">
        <div className="row align-items-center">
          <div className="col">
            <p className="h2 text-dark p-2">Permission</p>
          </div>
          <div className="col d-flex align-items-center">
            <label className="text-dark h5 mx-3">Groups:</label>
            <select
              id="group-select"
              className="form-control"
              value={selectedGroup || ''}
              onChange={(e) => handleGroupChange(e.target.value)}
            >
              <option value="" disabled>
                Select a group
              </option>
              {groupList.map((group) => (
                <option key={group.id} value={group.name}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <table className="styled-table mt-4">
          <thead>
            <tr>
              <th>Model Name</th>
              <th>Create</th>
              <th>Edit</th>
              <th>Show</th>
              <th>Delete</th>
              <th>First Approval</th>
              <th>Review</th>
              <th>Second Approval</th>
            </tr>
          </thead>
          <tbody>
            {modelName.map((name) => (
              <tr key={name}>
                <td>{name}</td>
                {['create', 'edit', 'show', 'delete', 'First Approval', 'Review', 'Second Approval'].map(
                  (type) => (
                    <td 
                      key={`${name}_${type}`} 
                      onClick={() => handleToggle(name, type)} 
                      style={{ cursor: 'pointer' }}
                    >
                      {permissions[`${name}_${type}`] ? (
                        <PiToggleRightThin size={24} color="green" />
                      ) : (
                        <PiToggleLeftThin size={24} color="red" />
                      )}
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="text-center mt-4">
          <button
            onClick={submitPermissionGroup}
            className="mx-3 btnSubmit " 

            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionPages;
